"use client";

import { useEffect, useLayoutEffect, useRef, useState } from "react";

type MasonryRevealProps = {
  children: React.ReactNode;
  showMoreLabel: string;
};

/** Cards shown before the first "Show more" tap on the single-column mobile feed. */
const MOBILE_INITIAL_CARDS = 6;
/** Extra cards revealed per tap on mobile — a paced reveal, not the whole feed. */
const MOBILE_STEP_CARDS = 6;
/** Desktop rows shown per column before the button (3 columns ≈ 12 cards). */
const DESKTOP_VISIBLE_ROWS = 4;

/**
 * Height (px) from the top of `el` to the bottom of its `count`-th card, or null
 * once every card fits within `count` (no clip needed). On mobile the columns
 * stack in document order, so the count-th `<li>`'s bottom is the visual clip.
 */
const clipHeightFor = (el: HTMLElement, count: number) => {
  const cards = el.querySelectorAll("li");
  if (count >= cards.length) return null;
  const last = cards[count - 1];
  if (!last) return null;
  return Math.round(last.getBoundingClientRect().bottom - el.getBoundingClientRect().top);
};

/**
 * Desktop clip height: the lowest bottom edge of the `rows`-th card across the
 * masonry columns, so exactly `rows` cards per column (≈ 3×rows total) show
 * before the fade. Returns null if there aren't multiple columns yet.
 */
const desktopClipFor = (el: HTMLElement, rows: number) => {
  const columns = el.querySelectorAll<HTMLElement>(".reviews-masonry__col");
  if (columns.length < 2) return null;
  const top = el.getBoundingClientRect().top;
  let maxBottom = 0;
  columns.forEach((column) => {
    const cards = column.querySelectorAll<HTMLElement>("li");
    const nth = cards[rows - 1] ?? cards[cards.length - 1];
    if (nth) maxBottom = Math.max(maxBottom, nth.getBoundingClientRect().bottom - top);
  });
  return maxBottom > 0 ? Math.round(maxBottom) : null;
};

/**
 * Collapses the masonry feed with a fade-up gradient and a "Show more" button.
 *
 * Desktop (three columns): collapses to about the top half; one tap animates the
 * max-height up to the full content height, then drops the clip.
 *
 * Mobile (single stacked column): shows the first {@link MOBILE_INITIAL_CARDS}
 * cards, and each tap reveals {@link MOBILE_STEP_CARDS} more with a smooth
 * max-height transition — a paced reveal rather than dumping the whole feed.
 */
const MasonryReveal = ({ children, showMoreLabel }: MasonryRevealProps) => {
  const contentRef = useRef<HTMLDivElement>(null);

  const [isMobile, setIsMobile] = useState(false);
  const [cardCount, setCardCount] = useState(0);

  // Desktop: collapse to ~top half, one tap reveals the rest.
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const [expanded, setExpanded] = useState(false);
  const [targetHeight, setTargetHeight] = useState<number | null>(null);

  // Mobile: reveal MOBILE_STEP_CARDS more per tap, animating each step.
  const [visibleCards, setVisibleCards] = useState(MOBILE_INITIAL_CARDS);
  const [mobileClip, setMobileClip] = useState<number | null>(null);
  const [animating, setAnimating] = useState(false);
  const [pendingReveal, setPendingReveal] = useState<{ clip: number; final: boolean } | null>(null);

  const [fullyOpen, setFullyOpen] = useState(false);

  // Refs so the ResizeObserver closure stays stable but reads live values.
  const liveRef = useRef({ visibleCards, animating, fullyOpen });
  liveRef.current = { visibleCards, animating, fullyOpen };

  useLayoutEffect(() => {
    const el = contentRef.current;
    if (!el) return;

    const measure = () => {
      const mobile = window.matchMedia("(max-width: 767px)").matches;
      setIsMobile(mobile);
      setCardCount(el.querySelectorAll("li").length);

      // Never re-clip mid-animation or after the feed has been fully opened.
      const live = liveRef.current;
      if (live.fullyOpen || live.animating) return;

      if (mobile) {
        setMobileClip(clipHeightFor(el, live.visibleCards));
      } else {
        setCollapsedHeight(
          desktopClipFor(el, DESKTOP_VISIBLE_ROWS) ?? Math.round(el.scrollHeight * 0.55),
        );
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    window.addEventListener("resize", measure);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, []);

  // Desktop opt-in: one tick after the collapsed height paints, grow to the full
  // content height, then drop the clip. setTimeout (not rAF) so it still fires in
  // a backgrounded tab where rAF is paused.
  useEffect(() => {
    if (!expanded) return;
    const el = contentRef.current;
    const grow = window.setTimeout(() => setTargetHeight(el ? el.scrollHeight : 100000), 0);
    const release = window.setTimeout(() => setFullyOpen(true), 700);
    return () => {
      window.clearTimeout(grow);
      window.clearTimeout(release);
    };
  }, [expanded]);

  // Mobile step: the tap flips `animating` (applies the transition class) while
  // the old clip is still painted; a tick later we bump the clip so the browser
  // animates between the two heights. The settle timer is a backstop for when
  // transitionend never fires (reduced motion, backgrounded tab).
  useEffect(() => {
    if (!pendingReveal) return;
    const grow = window.setTimeout(() => setMobileClip(pendingReveal.clip), 0);
    const settle = window.setTimeout(() => {
      setAnimating(false);
      if (pendingReveal.final) setFullyOpen(true);
    }, 700);
    return () => {
      window.clearTimeout(grow);
      window.clearTimeout(settle);
    };
  }, [pendingReveal]);

  const revealMore = () => {
    const el = contentRef.current;
    if (!el) return;
    const total = el.querySelectorAll("li").length;
    const next = Math.min(visibleCards + MOBILE_STEP_CARDS, total);
    const final = next >= total;
    // On the final step grow to the full height so the last card isn't clipped.
    const clip = final ? el.scrollHeight : (clipHeightFor(el, next) ?? el.scrollHeight);
    setVisibleCards(next);
    setAnimating(true);
    setPendingReveal({ clip, final });
  };

  const showDesktopButton = !isMobile && !expanded && collapsedHeight !== null;
  const showMobileButton = isMobile && !fullyOpen && cardCount > 0 && visibleCards < cardCount;
  const showButton = showDesktopButton || showMobileButton;

  let maxHeight: string | undefined;
  if (fullyOpen) maxHeight = undefined;
  else if (isMobile) maxHeight = mobileClip === null ? undefined : `${mobileClip}px`;
  else if (expanded) maxHeight = `${targetHeight ?? collapsedHeight ?? 0}px`;
  else if (collapsedHeight !== null) maxHeight = `${collapsedHeight}px`;

  const transitioning = isMobile ? animating : expanded && !fullyOpen;

  const clipPx = isMobile ? mobileClip : collapsedHeight;
  const fadeHeight = clipPx === null ? 0 : Math.min(Math.round(clipPx * 0.5), 180);

  return (
    <div className="relative">
      <div
        className={`overflow-hidden ${transitioning ? "reviews-masonry-reveal--expanding" : ""}`}
        onTransitionEnd={(event) => {
          if (event.target !== contentRef.current || event.propertyName !== "max-height") return;
          if (isMobile) {
            setAnimating(false);
            if (visibleCards >= cardCount) setFullyOpen(true);
          } else {
            setFullyOpen(true);
          }
        }}
        ref={contentRef}
        style={{ maxHeight }}
      >
        {children}
      </div>

      {showButton && (
        <div
          className="reviews-masonry__fade pointer-events-none absolute inset-x-0 bottom-0 flex flex-col items-center justify-end"
          style={{ height: `${fadeHeight}px` }}
        >
          <button
            className="bg-mocha text-champagne-white hover:bg-mocha/90 pointer-events-auto mb-2 px-7 py-3 text-sm font-normal transition-colors"
            onClick={isMobile ? revealMore : () => setExpanded(true)}
            type="button"
          >
            {showMoreLabel}
          </button>
        </div>
      )}
    </div>
  );
};

export default MasonryReveal;
